import { RefObject, useEffect, useMemo, useState } from 'react';

type UseOnScreenResult = boolean;

/**
 * Custom hook to check if the specified element is visible on the screen.
 * @param {RefObject<HTMLElement>} ref - Reference to the element to be monitored.
 * @returns {boolean} - Whether the element is visible on the screen.
 */
export default function useOnScreen(
  ref: RefObject<HTMLElement | null>,
): UseOnScreenResult {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(() => {
    if (typeof IntersectionObserver !== 'undefined') {
      return new IntersectionObserver(([entry]) =>
        setIntersecting(entry.isIntersecting),
      );
    }
    return null;
  }, []);

  useEffect(() => {
    if (!observer || !ref?.current) return;
    observer.observe(ref.current as Element);
    return () => observer.disconnect();
  }, [ref, observer]);

  return isIntersecting;
}
